import { useEffect, useState } from "react";
import { Megaphone, Mail, MessageSquare, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { api, fmtErr } from "../../lib/api";

export default function MarketingTab({ restaurant }) {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ name: "", channel: "email", subject: "", message: "", min_visits: 0, lapsed_days: 0, vip_only: false });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [winback, setWinback] = useState(Boolean(restaurant.winback_enabled));
  const [winbackDays, setWinbackDays] = useState(restaurant.winback_days || 60);

  const saveWinback = async () => {
    try {
      await api.put(`/restaurants/${restaurant.id}`, { winback_enabled: winback, winback_days: Number(winbackDays) || 60 });
      toast.success("Win-back automation saved — the daily scheduler takes it from here");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const load = () => api.get(`/restaurants/${restaurant.id}/campaigns`).then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, [restaurant.id]);

  const segBody = () => ({ ...form, min_visits: Number(form.min_visits) || 0, lapsed_days: Number(form.lapsed_days) || 0 });

  const doPreview = async () => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/campaigns/preview`, segBody());
      setPreview(data.count);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const send = async () => {
    if (!form.name.trim()) { toast.error("Give the campaign a name"); return; }
    if (!form.message.trim()) { toast.error("Write a message first"); return; }
    if (form.channel === "email" && !form.subject.trim()) { toast.error("Subject is required for email campaigns"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/campaigns`, segBody());
      toast.success(`Sending to ${data.recipients} guest${data.recipients === 1 ? "" : "s"} in the background — history updates when done`);
      setForm({ name: "", channel: form.channel, subject: "", message: "", min_visits: 0, lapsed_days: 0, vip_only: false });
      setPreview(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setSending(false);
  };

  return (
    <div data-testid="marketing-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" /> New campaign
        </h3>
        <p className="text-xs text-muted-foreground mt-1">Email or SMS a segment of your guest base. Emails use your SMTP settings if configured.</p>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Campaign name</Label>
            <Input data-testid="campaign-name-input" placeholder="e.g. November tasting menu launch" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-background border-border/60" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Channel</Label>
            <div className="flex gap-2 mt-1.5">
              {[["email", Mail, "Email"], ["sms", MessageSquare, "SMS"]].map(([ch, Icon, label]) => (
                <button key={ch} data-testid={`campaign-channel-${ch}`} onClick={() => setForm({ ...form, channel: ch })}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${form.channel === ch ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>
          {form.channel === "email" && (
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Subject</Label>
              <Input data-testid="campaign-subject-input" placeholder="A little something for you..." value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            </div>
          )}
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Message</Label>
            <Textarea data-testid="campaign-message-input" rows={4} placeholder="Hi! We're launching something special..." value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-background border-border/60" />
            {form.channel === "sms" && <p className="text-[11px] text-muted-foreground mt-1">Keep it short — SMS uses your Twilio settings and platform texts add 10p per booking on your bill.</p>}
          </div>
          <div className="rounded-xl border border-border/40 p-4 space-y-3">
            <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Segment</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Min visits</Label>
                <Input data-testid="seg-min-visits" type="number" min="0" value={form.min_visits}
                  onChange={(e) => setForm({ ...form, min_visits: e.target.value })} className="mt-1 bg-background border-border/60 h-9" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Lapsed (days)</Label>
                <Input data-testid="seg-lapsed-days" type="number" min="0" value={form.lapsed_days}
                  onChange={(e) => setForm({ ...form, lapsed_days: e.target.value })} className="mt-1 bg-background border-border/60 h-9" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">VIP guests only</Label>
              <Switch data-testid="seg-vip-switch" checked={form.vip_only} onCheckedChange={(v) => setForm({ ...form, vip_only: v })} />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button data-testid="campaign-preview-btn" size="sm" variant="outline" className="rounded-full h-8" onClick={doPreview}>
                Preview audience
              </Button>
              {preview !== null && <span data-testid="campaign-preview-count" className="text-sm text-primary font-medium">{preview} guests match</span>}
            </div>
          </div>
          <Button data-testid="campaign-send-btn" onClick={send} disabled={sending} className="w-full rounded-full">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send campaign"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg">Campaign history</h3>
        <div className="mt-4 space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-row-${c.id}`} className="rounded-xl border border-border/40 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">{c.name}</p>
                <Badge variant="outline" className={c.channel === "sms" ? "text-primary border-primary/40" : ""}>{c.channel}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.message}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {c.sent} sent{c.skipped ? ` · ${c.skipped} skipped` : ""} · {(c.sent_at || c.created_at || "").slice(0, 16).replace("T", " ")}
              </p>
            </div>
          ))}
          {campaigns.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-campaigns-msg">No campaigns sent yet.</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-6 lg:col-span-2" data-testid="winback-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-outfit font-medium text-lg">Win-back automation</h3>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-xl">
              Automatically email guests who haven't visited in a while — at most one message per guest every 60 days, sent by the daily scheduler.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Lapsed after</Label>
              <Input data-testid="winback-days-input" type="number" min="14" max="365" value={winbackDays}
                onChange={(e) => setWinbackDays(e.target.value)} className="w-20 h-9 bg-background border-border/60" />
              <span className="text-xs text-muted-foreground">days</span>
            </div>
            <Switch data-testid="winback-switch" checked={winback} onCheckedChange={setWinback} />
          </div>
        </div>
        <Button data-testid="winback-save-btn" size="sm" className="rounded-full mt-4" onClick={saveWinback}>Save automation</Button>
      </div>
    </div>
  );
}
